/** Subtask step UI for the welcome onboarding flow (first step + skip/done). */
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import { useT } from '@/i18n/useT';
import { PlusIcon } from '@/features/tasks/components/atoms/PlusIcon';
import '@/features/tasks/components/items/TaskItem.css';
import './OnboardingWelcomeSteps.css';
import { getOnboardingHighlightSx } from '@/features/onboarding/utils/onboardingHighlightSx';
import { ONBOARDING_WELCOME_STEP_TEST_IDS } from '@/features/onboarding/constants/welcomeStepTestIds';

const SUBTASK_INDENT_LEVEL = 1;

/**
 * @param {Object} props
 * @param {string} props.parentTitle
 * @param {import('@/types').Task | undefined} props.parentTask
 * @param {{ id: string, title: string } | null} props.addedSubtask
 * @param {boolean} props.subtaskEditing
 * @param {boolean} props.subtaskSaving
 * @param {string} props.subtaskInput
 * @param {(value: string) => void} props.setSubtaskInput
 * @param {() => void} props.onStartSubtaskEdit
 * @param {() => void | Promise<void>} props.onSubtaskEnter
 * @param {() => void | Promise<void>} props.onSubtaskPrimary
 */
const OnboardingWelcomeSubtaskStep = ({
  parentTitle,
  parentTask,
  addedSubtask,
  subtaskEditing,
  subtaskSaving,
  subtaskInput,
  setSubtaskInput,
  onStartSubtaskEdit,
  onSubtaskEnter,
  onSubtaskPrimary,
}) => {
  const t = useT();
  // Hide once + is used (editing) or a subtask exists; keep layout via visibility.
  const showPlusHint = !addedSubtask && !subtaskEditing;

  return (
    <Box data-testid={ONBOARDING_WELCOME_STEP_TEST_IDS.subtask}>
      <Typography variant="h5" component="h1" gutterBottom>
        {t('onboarding.subtaskStep.title', 'Wat is de eerste stap?')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t(
          'onboarding.subtaskStep.body',
          'Begin met de eerste stap, dan is starten makkelijker.',
        )}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        aria-hidden={!showPlusHint}
        sx={{
          mb: 2,
          // Keep layout height when hint is inactive (card-height soft-held elsewhere).
          visibility: showPlusHint ? 'visible' : 'hidden',
        }}
      >
        {t(
          'onboarding.subtaskStep.plusHint',
          'Druk op + om een subtaak toe te voegen.',
        )}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          mb: 1,
        }}
      >
        <Typography variant="body1" sx={{ flex: 1 }}>
          {parentTitle || parentTask?.title}
        </Typography>
        {!addedSubtask ? (
          <IconButton
            size="small"
            aria-label={t('taskitem.addSubtask', 'Add subtask')}
            data-testid="onboarding-welcome-subtask-trigger"
            onClick={onStartSubtaskEdit}
            sx={getOnboardingHighlightSx(!subtaskEditing)}
          >
            <PlusIcon />
          </IconButton>
        ) : null}
      </Box>
      {addedSubtask ? (
        <div
          className="subtask-input-wrapper onboarding-welcome-subtask-row"
          style={/** @type {any} */ ({ '--level': SUBTASK_INDENT_LEVEL })}
          data-testid="onboarding-welcome-subtask-row"
        >
          <span className="onboarding-welcome-subtask-gutter" aria-hidden="true">
            ↳
          </span>
          <Typography variant="body1">{addedSubtask.title}</Typography>
        </div>
      ) : null}
      {subtaskEditing && !addedSubtask ? (
        <>
          <div
            className="subtask-input-wrapper onboarding-welcome-subtask-row"
            style={/** @type {any} */ ({ '--level': SUBTASK_INDENT_LEVEL })}
            data-testid="onboarding-welcome-subtask-input-row"
          >
            <span className="onboarding-welcome-subtask-gutter" aria-hidden="true">
              ↳
            </span>
            <TextField
              fullWidth
              size="small"
              autoFocus
              value={subtaskInput}
              onChange={(e) => setSubtaskInput(e.target.value)}
              placeholder={t(
                'onboarding.subtaskStep.placeholder',
                'Bijvoorbeeld: cijfers opzoeken',
              )}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void onSubtaskEnter();
                }
              }}
            />
          </div>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 0.5 }}
          >
            {t('onboarding.subtaskStep.enterHint', 'Enter om toe te voegen')}
          </Typography>
        </>
      ) : null}
      <Button
        variant={addedSubtask ? 'contained' : 'text'}
        size="small"
        disabled={subtaskSaving}
        onClick={() => void onSubtaskPrimary()}
        sx={{ mt: 2 }}
      >
        {addedSubtask
          ? t('onboarding.subtaskStep.done', 'Klaar')
          : t('onboarding.subtaskStep.skip', 'Overslaan')}
      </Button>
    </Box>
  );
};

export default OnboardingWelcomeSubtaskStep;
